import React, { useState, useEffect } from 'react';
import { WorkflowTemplate } from '../../types';
import { MagnifyingGlassIcon, UserIcon, CalendarIcon, EyeIcon, TagIcon, CubeIcon, BookmarkIcon } from '@heroicons/react/24/outline';
import { apiService } from '../../services/apiService';
import { WorkflowMiniPreview } from './WorkflowMiniPreview';
import toast from '../../utils/toast';

interface WorkflowTemplateLibraryProps {
  onSelectTemplate?: (template: WorkflowTemplate) => void;
  onClose?: () => void;
}

export const WorkflowTemplateLibrary: React.FC<WorkflowTemplateLibraryProps> = ({
  onSelectTemplate,
  onClose,
}) => {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showOnlyMine, setShowOnlyMine] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, [searchTerm, selectedCategory, showOnlyMine]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm || undefined,
        category: selectedCategory || undefined,
        onlyMine: showOnlyMine || undefined
      };

      const templates = await apiService.getAllWorkflowTemplates(params);
      setTemplates(templates || []);
    } catch (error) {
      toast.error('Error loading workflow templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template: WorkflowTemplate) => {
    onSelectTemplate?.(template);
    onClose?.();
  };

  const getAvailableCategories = () => {
    const categories = new Set<string>();
    templates.forEach(template => {
      if (template.category) {
        categories.add(template.category);
      }
    });
    return Array.from(categories);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Workflow Template Library</h2>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ✕
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Category Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Categories</option>
          {getAvailableCategories().map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>

        {/* My Templates Toggle */}
        <div className="flex items-center">
          <input
            id="only-mine"
            type="checkbox"
            checked={showOnlyMine}
            onChange={(e) => setShowOnlyMine(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="only-mine" className="ml-2 text-sm text-gray-700">
            Only my templates
          </label>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {templates.map(template => (
            <div
              key={template.id}
              onClick={() => handleTemplateSelect(template)}
              className="group relative bg-white rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-lg border-gray-200 hover:border-gray-300 overflow-hidden"
              style={{ borderLeftColor: '#8B5CF6', borderLeftWidth: '4px' }}
            >
              {/* Template Layout: Left Preview + Right Content */}
              <div className="flex h-48">
                {/* Left Side - Mini Preview */}
                <div className="w-2/5 p-3 flex items-center justify-center bg-gray-50">
                  {template.nodeTemplates && template.nodeTemplates.length > 0 ? (
                    <WorkflowMiniPreview
                      nodeTemplates={template.nodeTemplates || []}
                      edgeTemplates={template.edgeTemplates || []}
                      width={140}
                      height={120}
                      className="shadow-sm"
                    />
                  ) : (
                    <div className="w-[140px] h-[120px] bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                      <div className="text-center text-gray-400">
                        <BookmarkIcon className="h-8 w-8 mx-auto mb-1" />
                        <div className="text-xs">No Preview</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Side - Template Info */}
                <div className="w-3/5 p-4 flex flex-col justify-between">
                  {/* Header */}
                  <div className="mb-2">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">
                        {template.title}
                      </h3>
                      <span className="ml-2 bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                        Template
                      </span>
                    </div>
                    {template.description && (
                      <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                        {template.description}
                      </p>
                    )}
                  </div>

                  {/* Details */}
                  <div className="space-y-1.5">
                    {template.category && (
                      <div className="text-xs text-gray-500 flex items-center">
                        <CubeIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{template.category}</span>
                      </div>
                    )}

                    <div className="flex items-center text-xs text-gray-500">
                      <UserIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{template.createdByName || 'Unknown'}</span>
                    </div>

                    <div className="flex items-center text-xs text-gray-500">
                      <CalendarIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                      {new Date(template.createdAt).toLocaleDateString()}
                    </div>

                    {template.tags && (
                      <div className="flex items-start text-xs text-gray-500">
                        <TagIcon className="h-3 w-3 mr-1 flex-shrink-0 mt-0.5" />
                        <div className="flex flex-wrap gap-1">
                          {template.tags.split(',').slice(0, 3).map((tag: string, index: number) => (
                            <span
                              key={index}
                              className="inline-block bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded text-xs"
                            >
                              {tag.trim()}
                            </span>
                          ))}
                          {template.tags.split(',').length > 3 && (
                            <span className="text-gray-400 text-xs">
                              +{template.tags.split(',').length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Template Action Button */}
              <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Handle template preview
                  }}
                  className="p-1 text-gray-400 hover:text-purple-600 hover:bg-white rounded shadow-sm"
                  title="Preview Template"
                >
                  <EyeIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Selection Indicator */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-purple-300 rounded-lg pointer-events-none transition-colors" />
            </div>
          ))}
        </div>
      </div>

      {/* Empty State */}
      {templates.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <BookmarkIcon className="mx-auto h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No workflow templates found</h3>
          <p className="text-gray-500">
            {searchTerm || selectedCategory ? 'Try adjusting your filters' : 'Create your first workflow template to get started'}
          </p>
        </div>
      )}
    </div>
  );
};